import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Select, Switch } from 'antd';
import { BankOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AutoComplete from 'react-google-autocomplete';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useForm from '../../hooks/utility/useForm';
import SortableList from '../dnd/SortableList';
import { ORDER_METHODS, ROOM_SERVICE_LOCATION_TYPES } from '../../services/exports/Constants';
import GooglePlaceHelpers from '../../services/helpers/GooglePlaceHelpers';
import useApiClient from '../../hooks/api/useApiClient';
import LoadingIndicator from '../LoadingIndicator';
import DragNDropHelpers from '../../services/helpers/DragNDropHelpers';
import DeleteCancelModal from '../../modals/DeleteCancelModal';

export default function RoomServiceTab({ data, toggleMethodVisible, feedback, onChange, onSave }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:RoomServiceTab' });

  const { RoomServiceLocationsManager, CompaniesManager } = useApiClient();

  const { company } = useSelector((store) => store.currentCompany);

  const [googlePlaceSearch, setGooglePlaceSearch] = useState('');
  const {
    data: form,
    update,
    reset,
  } = useForm({
    type: ROOM_SERVICE_LOCATION_TYPES.table_service,
    name: null,
    street_name: null,
    street_number: null,
    city: null,
    zip_code: null,
    sorting_order: null,
  });

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [tableNumberHidden, setTableNumberHidden] = useState(company?.table_number_hidden);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    update('sorting_order', (collect(locations).last()?.sorting_order ?? 0) + 1);
  }, [locations]);

  const saveSettings = async () => {
    setSavingSettings(true);
    const { success } = await CompaniesManager.updateSettings({
      table_number_hidden: tableNumberHidden,
    });
    setSavingSettings(false);
    if (success) {
      return toast.success(t('toasts.saving_success'));
    }

    toast.error(t('toasts.saving_error'));
  };

  const onPlaceSelected = (place) => {
    const name = place.name.split(',')[0] ?? place.name;

    setGooglePlaceSearch(name);
    update('name', name);
    update('street_name', GooglePlaceHelpers.getStreetName(place));
    update('street_number', GooglePlaceHelpers.getStreetNumber(place));
    update('city', GooglePlaceHelpers.getCity(place));
    update('zip_code', GooglePlaceHelpers.getZipCode(place));
  };

  async function loadData() {
    setLoadingData(true);
    const { success, data: responseData } = await RoomServiceLocationsManager.get();
    setLoadingData(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_locations'));
    }

    setLocations(responseData);
  }

  async function createLocation() {
    setLoading(true);
    const { success, data: responseData } = await RoomServiceLocationsManager.create(form);
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_create_location'));
    }

    reset();
    setGooglePlaceSearch('');
    toast.success(t('toasts.location_got_created'));
    setLocations((current) => [...current, responseData]);
  }

  async function toggleLocationAvailability(location) {
    const { success, data: responseData } = await RoomServiceLocationsManager.update(location.id, {
      is_active: !location.is_active,
    });

    if (!success) {
      return toast.error(t('toasts.failed_to_toggle_location'));
    }

    setLocations((current) =>
      collect(current)
        .transform((item) => (item.id === location.id ? responseData : item))
        .toArray(),
    );
  }

  async function deleteLocation(location) {
    setLoading(true);
    const { success } = await RoomServiceLocationsManager.delete(location.id);
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_delete_location'));
    }

    setSelectedLocation(null);
    toast.success(t('toasts.location_got_deleted'));
    setLocations((current) =>
      collect(current)
        .reject((item) => item.id === location.id)
        .toArray(),
    );
  }

  async function saveLocationsOrder(order) {
    RoomServiceLocationsManager.reorder(collect(order).pluck('id').toArray());
  }

  function onDragEnd(event) {
    DragNDropHelpers.onDndKitDragEnd(
      locations,
      event,
      (res) => {
        setLocations(res);
        saveLocationsOrder(res);
      },
      (item) => item.id,
    );
  }

  function SortableLocationRow({ item }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      cursor: 'move',
      ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    return (
      <tr ref={setNodeRef} style={style} className="border-t" {...attributes} {...listeners}>
        <td className="py-4">{item.id}</td>
        <td className="py-4">{t(`room_service_types.${item.type}`)}</td>
        <td className="py-4">{item.name}</td>
        <td className="py-4">{`${item.street_name} ${item.street_number}`}</td>
        <td className="py-4">
          <Switch checked={item.is_active} onChange={() => toggleLocationAvailability(item)} />
        </td>
        <td className="text-brand-danger py-4 cursor-pointer hover:underline" onClick={() => setSelectedLocation(item)}>
          {t('buttons.delete')}
          <DeleteOutlined className="ml-xs my-auto" />
        </td>
      </tr>
    );
  }

  const renderLocations = () => {
    if (loadingData) {
      return <LoadingIndicator />;
    }

    return (
      <>
        <hr className="my-4" />
        <table className="w-full">
          <thead>
            <tr>
              <th className="py-4">{t('fields.id')}</th>
              <th className="py-4">{t('fields.type')}</th>
              <th className="py-4">{t('fields.name')}</th>
              <th className="py-4">{t('fields.address')}</th>
              <th className="py-4">{t('fields.is_active')}</th>
              <th />
            </tr>
          </thead>
          <SortableList items={locations.map((item) => item.id)} onDragEnd={onDragEnd}>
            <tbody>
              {locations.map((item) => (
                <SortableLocationRow key={`room-service-location-${item.id}`} item={item} />
              ))}
            </tbody>
          </SortableList>
        </table>
      </>
    );
  };

  const renderDeleteModal = useMemo(
    () =>
      selectedLocation && (
        <DeleteCancelModal
          show
          title={t('modals.delete_location.title')}
          description={t('modals.delete_location.description', { name: selectedLocation.name })}
          loading={loading}
          onConfirm={() => deleteLocation(selectedLocation)}
        />
      ),
    [selectedLocation, loading],
  );

  return (
    <div className="mt-lg">
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.state.title')}</div>
        <div>
          <div className="mb-md flex items-center">
            <Switch
              id="room-service-visible"
              checked={!data.hidden_order_methods.includes(ORDER_METHODS.room_service)}
              onChange={toggleMethodVisible}
            />
            <label htmlFor="room-service-visible" className="my-auto ml-4 cursor-pointer">
              {t('sections.state.fields.room_service_visible.label')}
            </label>
          </div>
          <div className="mb-md flex items-center">
            <Switch
              id="has-room-service"
              checked={data?.has_room_service}
              onChange={(checked) => onChange('has_room_service', checked)}
            />
            <label htmlFor="has-room-service" className="my-auto ml-4 cursor-pointer">
              {t('sections.state.fields.has_room_service.label')}
            </label>
            <div className="text-sm text-gray-500">{feedback?.errors?.has_room_service}</div>
          </div>
          <Button type="primary" loading={loading} disabled={loading} onClick={onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      {data?.has_room_service && (
        <Card className="mb-md">
          <div className="text-base font-semibold mb-md">{t('sections.settings.title')}</div>
          <div>
            <div className="mb-md flex items-center">
              <Switch
                id="table_number_hidden"
                checked={!tableNumberHidden}
                onChange={(checked) => setTableNumberHidden(!checked)}
              />
              <label htmlFor="table_number_hidden" className="my-auto ml-4 cursor-pointer">
                {t('sections.settings.table_number.label')}
              </label>
            </div>
            <Button
              type="primary"
              loading={savingSettings}
              disabled={savingSettings}
              onClick={saveSettings}
              className="my-4"
            >
              {t('buttons.save')}
            </Button>
          </div>
        </Card>
      )}
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.locations.title')}</div>
        <div>
          <div className="mb-md">
            <label htmlFor="google-place">{t('form.google_place.label')}</label>
            <div className="flex items-center w-full p-2 border rounded">
              <BankOutlined className="mr-xs my-auto" />
              <AutoComplete
                apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                onPlaceSelected={onPlaceSelected}
                onChange={(e) => setGooglePlaceSearch(e.target.value)}
                value={googlePlaceSearch}
                placeholder={t('form.google_place.placeholder')}
                className="w-full overflow-ellipsis border-none"
                options={{
                  fields: ['name', 'place_id', 'address_components'],
                  types: ['lodging', 'bar', 'cafe', 'restaurant', 'food'],
                  componentRestrictions: { country: company.country },
                }}
              />
            </div>
          </div>
          {googlePlaceSearch?.length > 3 && (
            <>
              <hr className="my-4" />
              <div className="mb-md">
                <label htmlFor="type">{t('form.type.label')}</label>
                <Select
                  id="type"
                  className="w-full"
                  value={form.type}
                  onChange={(value) => update('type', value)}
                  options={Object.values(ROOM_SERVICE_LOCATION_TYPES).map((item) => ({
                    value: item,
                    label: t(`form.type.options.${item}`),
                  }))}
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.type}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="name">{t('form.name.label')}</label>
                <Input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder={t('form.name.placeholder')}
                  id="name"
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.name}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="street-name">{t('form.street_name.label')}</label>
                <Input
                  value={form.street_name}
                  onChange={(e) => update('street_name', e.target.value)}
                  placeholder={t('form.street_name.placeholder')}
                  id="street-name"
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.street_name}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="street-number">{t('form.street_number.label')}</label>
                <Input
                  value={form.street_number}
                  onChange={(e) => update('street_number', e.target.value)}
                  placeholder={t('form.street_number.placeholder')}
                  id="street-number"
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.street_number}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="city">{t('form.city.label')}</label>
                <Input
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  placeholder={t('form.city.placeholder')}
                  id="city"
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.city}</div>
              </div>
              <div className="mb-md">
                <label htmlFor="zip-code">{t('form.zip_code.label')}</label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => update('zip_code', e.target.value)}
                  placeholder={t('form.zip_code.placeholder')}
                  id="zip-code"
                />
                <div className="text-sm text-gray-500">{feedback?.errors?.zip_code}</div>
              </div>
              <Button type="primary" onClick={createLocation} loading={loading}>
                {t('buttons.save')}
              </Button>
            </>
          )}
          {renderLocations()}
        </div>
      </Card>
      {renderDeleteModal}
    </div>
  );
}
